<template>
  <div class="ac-data-table-toolbar">
    <v-toolbar flat>
      <v-toolbar-title>{{ title }}</v-toolbar-title>
      <v-divider class="mx-2" inset vertical/>

      <slot name="actions"/>
      <v-spacer/>

      <slot name="search-prepend"/>

      <v-text-field
          v-model.trim="searchSync"
          append-icon="mdi-magnify"
          :label="searchLabel"
          hide-details
          single-line
          clearable
          class="ml-2"
      />

      <slot name="search-append"/>
    </v-toolbar>

    <div v-if="showBulk" class="d-flex pl-4 pb-1 align-center">
      <v-checkbox
          v-model="bulkModeSync"
          hide-details
          class="mt-0 pt-0"
      />

      <v-btn
          x-small
          class="ma-2 white--text"
          color="red"
          :disabled="!bulkModeSync"
          @click="onBulkRemove"
      >
        {{ bulkDeleteLabel }}
        <span v-if="deletableCountLocal">&nbsp;({{ deletableCountLocal }})</span>
      </v-btn>

      <slot name="bulk-extra"/>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AcDataTableToolbar',

  props: {
    title: { type: String, default: 'Table' },
    search: { type: String, default: '' },
    searchLabel: { type: String, default: 'Search' },

    showBulk: { type: Boolean, default: true },
    bulkMode: { type: Boolean, default: false },
    bulkDeleteLabel: { type: String, default: 'Bulk delete' },

    selected: { type: Array, default: () => [] },
    canDelete: { type: Function, default: () => true },

    // old mode (one-by-one)
    remove: { type: Function, default: null },

    // NEW mode (one request)
    // signature: async (items:Array) => Promise
    removeBulk: { type: Function, default: null },

    emptyMsg: { type: [String, Function], default: 'Select item(s) to delete' },
    confirmMsg: { type: Function, default: (count) => `Delete ${count} selected item(s)?` },
    successMsg: { type: String, default: 'Changes was published' },
    alertTitle: { type: String, default: 'Bulk delete' },

    deletableCount: { type: Number, default: null },
  },

  data: () => ({
    searchInner: '',
    bulkModeInner: false
  }),

  created() {
    this.searchInner = this.search;
    this.bulkModeInner = this.bulkMode;
  },

  computed: {
    searchSync: {
      get() { return this.searchInner; },
      set(v) { this.searchInner = v; this.$emit('update:search', v); }
    },

    bulkModeSync: {
      get() { return this.bulkModeInner; },
      set(v) { this.bulkModeInner = v; this.$emit('update:bulkMode', v); }
    },

    deletableList() {
      return (this.selected || []).filter(this.canDelete);
    },

    deletableCountLocal() {
      return this.deletableCount != null ? this.deletableCount : this.deletableList.length;
    }
  },

  watch: {
    search(v) { this.searchInner = v; },
    bulkMode(v) { this.bulkModeInner = v; },
    showBulk(v) {
      if (!v && this.bulkModeInner) this.$emit('update:bulkMode', false);
    }
  },

  methods: {
    async onBulkRemove() {
      const toDelete = this.deletableList.slice();

      if (!toDelete.length) {
        const msg = typeof this.emptyMsg === 'function' ? this.emptyMsg() : this.emptyMsg;
        this.$alert(msg);
        return;
      }

      try {
        await this.$confirm(this.confirmMsg(toDelete.length), this.alertTitle);
      } catch {
        return;
      }

      this.$emit('clear-selection');

      try {
        if (typeof this.removeBulk === 'function') {
          await this.removeBulk(toDelete);
        }
        else if (typeof this.remove === 'function') {
          await Promise.all(toDelete.map(item => this.remove(item)));
        } else {
          this.$emit('bulk-delete');
        }

        this.$alert(this.successMsg, this.alertTitle, 'success');
      } catch (e) {
        this.$alert(e?.message || e, this.alertTitle, 'error');
      }
    }
  }
};
</script>